/* ==========================================================================
   STR8 SPARKLES v1.0

   A sparse drifting particle field, drawn on a canvas, used as a background
   layer behind the buy panel.

   Opt in from the markup, the same way str8-reveal.js works:

     <canvas class="..." data-str8-sparkles
             data-str8-sparkle-count="52"
             data-str8-sparkle-speed="0.14"
             aria-hidden="true"></canvas>

   Both data attributes are optional. Colours are read from the --str8-gold
   and --str8-gold-muted custom properties so the field follows the palette
   in str8-foundation.css.liquid rather than hardcoding it a second time.

   Three gates, all of them necessary:

     1. IntersectionObserver. The panel sits low on the page, so without
        this the animation loop would run from page load whether or not
        anyone can see it.
     2. visibilitychange. A requestAnimationFrame loop in a backgrounded
        tab is wasted battery.
     3. prefers-reduced-motion. CSS "animation: none" does NOT stop a
        canvas loop, so reduced motion has to be honoured here in script as
        well as in the stylesheet.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__str8SparklesLoaded) return;
  window.__str8SparklesLoaded = true;

  var DEFAULT_COUNT = 52;
  var DEFAULT_SPEED = 0.14;
  var MAX_DPR = 2;

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function readColour(el, prop, fallback) {
    var v = getComputedStyle(el).getPropertyValue(prop);
    v = v ? v.trim() : '';
    return v || fallback;
  }

  /* #c29b57 -> "194,155,87" so alpha can vary per particle per frame. */
  function toRgb(hex, fallback) {
    var m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m) return fallback;
    return parseInt(m[1], 16) + ',' + parseInt(m[2], 16) + ',' + parseInt(m[3], 16);
  }

  function Field(canvas) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var count = parseInt(canvas.dataset.str8SparkleCount, 10);
    if (isNaN(count)) count = DEFAULT_COUNT;

    var speed = parseFloat(canvas.dataset.str8SparkleSpeed);
    if (isNaN(speed)) speed = DEFAULT_SPEED;

    var golds = [
      toRgb(readColour(canvas, '--str8-gold', '#c29b57'), '194,155,87'),
      toRgb(readColour(canvas, '--str8-gold-muted', '#a07d3a'), '160,125,58')
    ];

    var particles = [];
    var w = 0;
    var h = 0;
    var dpr = 1;
    var raf = null;
    var visible = false;

    function seed() {
      particles = [];
      for (var i = 0; i < count; i++) {
        particles.push({
          x: Math.random(),
          y: Math.random(),
          r: 0.4 + Math.random() * 1.2,
          drift: (0.4 + Math.random() * 0.9) * speed,
          sway: (Math.random() - 0.5) * 0.02,
          /* Independent phase and rate, so the field never twinkles in unison. */
          phase: Math.random() * Math.PI * 2,
          rate: 0.6 + Math.random() * 1.4,
          colour: golds[Math.random() < 0.65 ? 0 : 1]
        });
      }
    }

    function resize() {
      var rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function frame(now) {
      raf = window.requestAnimationFrame(frame);
      if (!w || !h) return;

      ctx.clearRect(0, 0, w, h);
      var t = now / 1000;

      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];

        /* Slow upward drift, wrapping back in at the bottom. */
        p.y -= (p.drift / h) * 1.6;
        p.x += p.sway / w;
        if (p.y < -0.02) { p.y = 1.02; p.x = Math.random(); }
        if (p.x < -0.02) p.x = 1.02;
        if (p.x > 1.02) p.x = -0.02;

        /* Sine twinkle, biased low so the field stays subordinate to the
           product image and the copy. */
        var a = 0.18 + 0.42 * (0.5 + 0.5 * Math.sin(p.phase + t * p.rate));

        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(' + p.colour + ',' + a.toFixed(3) + ')';
        ctx.fill();
      }
    }

    function start() {
      if (raf !== null || reduced) return;
      raf = window.requestAnimationFrame(frame);
    }

    function stop() {
      if (raf === null) return;
      window.cancelAnimationFrame(raf);
      raf = null;
      if (w && h) ctx.clearRect(0, 0, w, h);
    }

    resize();
    seed();

    if (reduced) return;

    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { resize(); }).observe(canvas);
    } else {
      window.addEventListener('resize', resize);
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible && !document.hidden) start(); else stop();
      }, { threshold: 0 }).observe(canvas);
    } else {
      visible = true;
      start();
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else if (visible) start();
    });
  }

  function init(scope) {
    var nodes = (scope || document).querySelectorAll('canvas[data-str8-sparkles]');
    Array.prototype.forEach.call(nodes, function (c) {
      if (c.dataset.str8SparklesBound === 'true') return;
      c.dataset.str8SparklesBound = 'true';
      Field(c);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor re-renders a section on every settings change. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
